import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Film, Loader2, RotateCcw, Trash2, Video } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { compress720, getDuration, MAX_MB, MAX_SECONDS, MAX_VIDEOS, uploadVideo, validType } from "@/lib/videoUpload";

const MB = 1024 * 1024;
const COMPRESS_OVER = 50 * MB;
const TIPS = ["Caballo en un lugar llano y despejado", "Graba de lado, de frente y por detrás", "Al paso y al trote en línea recta", "Buena luz, sin zoom y con la cámara fija"];
const LABEL = { checking: "Comprobando el vídeo…", compressing: "Reduciendo a 720p", uploading: "Subiendo", processing: "Analizando el movimiento…", ready: "Listo para el diagnóstico" };
export const busyVideo = (v) => !["ready", "error"].includes(v.status);

const VideoItem = ({ v, i, onRemove, onRetry, onRedo }) => (
  <li className="border border-slate-200 rounded p-3 bg-white" data-testid={`dx-video-item-${i}`}>
    <video src={v.url} controls playsInline muted className="w-full max-h-48 rounded bg-black" data-testid={`dx-video-preview-${i}`} />
    <div className="mt-2 flex items-center gap-2 text-sm">
      {v.status === "ready" ? <CheckCircle2 size={16} className="text-emerald-600" /> : v.status === "error" ? <AlertTriangle size={16} className="text-[#DC2626]" /> : <Loader2 size={16} className="animate-spin text-[#2563EB]" />}
      <p className={`flex-1 font-semibold ${v.status === "error" ? "text-[#DC2626]" : "text-[#0B1D3A]"}`} data-testid={`dx-video-status-${i}`}>
        {v.status === "error" ? v.error : `${LABEL[v.status]}${["uploading", "compressing"].includes(v.status) ? ` ${v.progress}%` : ""}`}</p>
    </div>
    {["uploading", "compressing"].includes(v.status) && <div className="h-2 bg-slate-100 rounded-full mt-2 overflow-hidden"><div className="h-full bg-[#2563EB] transition-[width] duration-300" style={{ width: `${v.progress}%` }} data-testid={`dx-video-progress-${i}`} /></div>}
    <div className="flex flex-wrap gap-2 mt-2">
      {v.status === "error" && !v.fatal && <button type="button" onClick={onRetry} className="text-sm font-bold text-[#2563EB] flex items-center gap-1" data-testid={`dx-video-retry-${i}`}><RotateCcw size={14} /> Reintentar</button>}
      <button type="button" onClick={onRedo} className="text-sm font-bold text-[#0B1D3A] flex items-center gap-1" data-testid={`dx-video-redo-${i}`}><Video size={14} /> Repetir</button>
      <button type="button" onClick={onRemove} className="text-sm font-bold text-[#DC2626] flex items-center gap-1 ml-auto" data-testid={`dx-video-remove-${i}`}><Trash2 size={14} /> Eliminar</button>
    </div>
  </li>
);

export const VideoPicker = ({ user, videos, setVideos }) => {
  const [consent, setConsent] = useState(false);
  const cam = useRef(null);
  const gal = useRef(null);
  const redo = useRef(null);
  const upd = (key, patch) => setVideos((vs) => vs.map((x) => (x.key === key ? { ...x, ...patch } : x)));

  const upload = async (item) => {
    upd(item.key, { status: "uploading", progress: 0, error: "" });
    try {
      const data = await uploadVideo(item.upFile, item.st, (p) => upd(item.key, { progress: p, status: p >= 100 ? "processing" : "uploading" }));
      upd(item.key, { status: "ready", server: data });
    } catch (e) {
      const s = e?.response?.status;
      const fatal = e.fatal || (s >= 400 && s < 500 && s !== 408);
      upd(item.key, { status: "error", fatal, error: fatal ? (e.fatal ? e.message : errMsg(e)) : "Se ha cortado la conexión. Pulsa Reintentar para continuar donde se quedó." });
    }
  };

  const prepare = async (item) => {
    const fail = (error) => upd(item.key, { status: "error", fatal: true, error });
    const d = await getDuration(item.file);
    if (d && d > MAX_SECONDS + 0.5) return fail(`El vídeo dura ${Math.round(d)} s. El máximo es ${MAX_SECONDS} s: recórtalo o graba uno más corto.`);
    let f = item.file;
    if (f.size > COMPRESS_OVER) {
      upd(item.key, { status: "compressing", progress: 0 });
      try { f = await compress720(f, (p) => upd(item.key, { progress: p })); } catch { f = item.file; }
      if (f.size > MAX_MB * MB) return fail(`El vídeo pesa ${Math.round(item.file.size / MB)} MB y el máximo es ${MAX_MB} MB. Graba uno más corto o en calidad 1080p.`);
    }
    upd(item.key, { upFile: f });
    upload({ ...item, upFile: f });
  };

  const pick = (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    const replace = redo.current;
    redo.current = null;
    if (!files.length) return;
    const room = MAX_VIDEOS - videos.length + (replace ? 1 : 0);
    if (files.length > room) toast.error(`Puedes añadir hasta ${MAX_VIDEOS} vídeos por diagnóstico`);
    const items = [];
    for (const file of files.slice(0, room)) {
      if (!validType(file)) { toast.error("Formato no válido. Usa un vídeo MP4, MOV o WebM."); continue; }
      items.push({ key: `${Date.now()}-${Math.random()}`, file, url: URL.createObjectURL(file), status: "checking", progress: 0, st: {} });
    }
    if (replace) remove(replace);
    setVideos((vs) => [...vs, ...items]);
    items.forEach((it) => setTimeout(() => prepare(it), 0));
  };

  const remove = (v) => {
    URL.revokeObjectURL(v.url);
    v.st.cancelled = true;
    if (v.st.id) api.delete(`/dx-video/${v.st.id}`).catch(() => {});
    setVideos((vs) => vs.filter((x) => x.key !== v.key));
  };
  const redoVideo = (v) => { redo.current = v; cam.current?.click(); };
  const retry = (v) => (v.upFile ? upload(v) : prepare(v));

  if (!user) return (
    <div className="border-2 border-dashed border-slate-200 rounded p-4 text-sm text-slate-600" data-testid="dx-video-login">
      <p className="font-display text-lg font-bold text-[#0B1D3A] flex items-center gap-2"><Film size={20} /> Vídeo del movimiento</p>
      <p className="mt-1"><Link to="/login?next=/diagnostico" className="font-bold text-[#2563EB] underline">Inicia sesión</Link> para añadir hasta 3 vídeos (cojeras, al paso o al trote).</p>
    </div>
  );

  return (
    <div data-testid="dx-video">
      <p className="font-display text-lg font-bold text-[#0B1D3A] flex items-center gap-2"><Film size={20} /> Vídeo del movimiento <span className="text-sm font-normal text-slate-500">(opcional, hasta {MAX_VIDEOS})</span></p>
      <div className="mt-2 bg-[#EFF6FF] rounded p-3 text-sm" data-testid="dx-video-tips">
        <p className="font-bold text-[#0B1D3A]">Antes de grabar</p>
        <ul className="mt-1 space-y-0.5 text-slate-700">{TIPS.map((t) => <li key={t}>· {t}</li>)}</ul>
        <p className="text-xs text-slate-500 mt-1">MP4, MOV o WebM · máx. {MAX_SECONDS} s y {MAX_MB} MB por vídeo</p>
      </div>
      <label className="flex items-start gap-2.5 text-sm mt-3 cursor-pointer">
        <Checkbox checked={consent} onCheckedChange={(c) => setConsent(!!c)} className="mt-0.5" data-testid="dx-video-consent" />
        <span>Acepto que FAUNNY guarde mis vídeos de forma privada durante 30 días para este diagnóstico (puedo borrarlos antes). <Link to="/legal/privacidad" target="_blank" className="font-bold text-[#2563EB] underline">Política de privacidad</Link></span>
      </label>
      <input ref={cam} type="file" accept="video/*" capture="environment" onChange={pick} className="hidden" data-testid="dx-video-camera-input" />
      <input ref={gal} type="file" accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.m4v,.webm" multiple onChange={pick} className="hidden" data-testid="dx-video-gallery-input" />
      {videos.length < MAX_VIDEOS && (
        <div className="grid grid-cols-2 gap-3 mt-3">
          <button type="button" disabled={!consent} onClick={() => cam.current?.click()} data-testid="dx-video-record-button" className="flex flex-col items-center gap-1 bg-white border border-[#93C5FD] hover:border-[#2563EB] disabled:opacity-50 rounded py-4 text-[#0B1D3A] font-bold text-sm transition-colors"><Video size={24} /> Grabar vídeo</button>
          <button type="button" disabled={!consent} onClick={() => gal.current?.click()} data-testid="dx-video-upload-button" className="flex flex-col items-center gap-1 bg-white border border-[#93C5FD] hover:border-[#2563EB] disabled:opacity-50 rounded py-4 text-[#0B1D3A] font-bold text-sm transition-colors"><Film size={24} /> Subir vídeo</button>
        </div>
      )}
      {!consent && !videos.length && <p className="text-[11px] text-slate-500 mt-1.5">Marca la casilla para poder añadir vídeos.</p>}
      {!!videos.length && <ul className="space-y-3 mt-3">{videos.map((v, i) => <VideoItem key={v.key} v={v} i={i} onRemove={() => remove(v)} onRetry={() => retry(v)} onRedo={() => redoVideo(v)} />)}</ul>}
    </div>
  );
};

const SavedRow = ({ v, onDeleted }) => {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);
  const play = async () => {
    try { const { data } = await api.get(`/dx-video/${v.id}/file`, { responseType: "blob" }); setUrl(URL.createObjectURL(data)); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm("¿Eliminar este vídeo ahora?")) return;
    setBusy(true);
    try { await api.delete(`/dx-video/${v.id}`); toast.success("Vídeo eliminado"); onDeleted(); } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <li className="py-2 border-b border-slate-100 text-sm" data-testid={`dx-saved-video-${v.id}`}>
      <div className="flex items-center gap-2">
        <p className="flex-1" translate="no">{new Date(v.created_at).toLocaleDateString("es-ES")} · {v.seconds} s · se borra el {new Date(v.expires_at).toLocaleDateString("es-ES")}</p>
        {!url && <button type="button" onClick={play} className="font-bold text-[#2563EB]" data-testid={`dx-saved-play-${v.id}`}>Ver</button>}
        <button type="button" disabled={busy} onClick={del} className="font-bold text-[#DC2626] disabled:opacity-50" data-testid={`dx-saved-delete-${v.id}`}>{busy ? "Eliminando…" : "Eliminar"}</button>
      </div>
      {url && <video src={url} controls playsInline autoPlay muted className="w-full max-h-48 rounded bg-black mt-2" />}
    </li>
  );
};

export const SavedVideos = ({ refresh }) => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/dx-video/mine").then((r) => setRows(r.data.filter((v) => v.diagnosis_id))).catch(() => {});
  useEffect(() => { load(); }, [refresh]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!rows.length) return null;
  return (
    <details className="border border-slate-200 rounded p-3" data-testid="dx-saved-videos">
      <summary className="font-bold text-[#0B1D3A] cursor-pointer">Tus vídeos guardados ({rows.length})</summary>
      <ul className="mt-2">{rows.map((v) => <SavedRow key={v.id} v={v} onDeleted={load} />)}</ul>
    </details>
  );
};
